/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useU2000LUStateHandlers } from '../u2000Core/reducer/formReducer/U2000LUHandlers';
import type { U2000LUState } from '../u2000Core/reducer/formReducer/U2000LUState';
import type { U3301InitLU } from './U3301Service';

export type InitLU = U2000LUState['personalization'] &
  U3301InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3301AgendaFilters {
  categoryIds: string[];
  searchText: string;
}

export interface U3301ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  initLU: U3301InitLU;
  refreshInitLU: () => void;
  agendaFilters: U3301AgendaFilters;
  setAgendaFilters: React.Dispatch<React.SetStateAction<U3301AgendaFilters>>;
}

interface U3301ContextProviderProps {
  initLU: U3301InitLU;
  refreshInitLU: () => void;
  children: ReactNode;
}

const U3301Context = React.createContext<U3301ContextType | undefined>(undefined);

export const U3301ContextProvider: React.FC<U3301ContextProviderProps> = ({ initLU, refreshInitLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const [agendaFilters, setAgendaFilters] = useState<U3301AgendaFilters>({
    categoryIds: [],
    searchText: '',
  });

  const contextValue = useMemo<U3301ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      initLU,
      refreshInitLU,
      agendaFilters,
      setAgendaFilters,
    }),
    [U2000LUStateHandlers, initLU, refreshInitLU, agendaFilters],
  );

  return <U3301Context.Provider value={contextValue}>{children}</U3301Context.Provider>;
};

export function useU3301Context(): U3301ContextType {
  const context = useContext(U3301Context);
  if (!context) {
    throw new Error('useU3301Context must be used within a U3301ContextProvider');
  }
  return context;
}
